import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Box,
  Typography,
  LinearProgress,
  Chip,
} from 'components/mui';
import { AddIcon, RemoveIcon } from 'components/icons';
import { useAnnouncementList } from 'containers/pages/announcementManagement/hooks';
import { getComparator, stableSort } from 'libs/sortList';
import AnnouncementFormUnified from '../announcementFormUnified/AnnouncementFormUnified';
import { Announcement } from 'containers/pages/announcementManagement/types';

const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

interface AnnouncementListProps {
  announcements: Announcement[];
  onDelete: (id: number) => Promise<void>;
  onRefresh: () => void;
}

function getStatus(expireDate: string): 'Active' | 'Expired' {
  if (!expireDate) return 'Expired';
  const expire = new Date(expireDate);
  const now = new Date();
  return expire >= now ? 'Active' : 'Expired';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-CA');
  } catch {
    return dateStr;
  }
}

function AnnouncementList({ announcements, onDelete, onRefresh }: AnnouncementListProps) {
  const { state, handler } = useAnnouncementList();
  const listRef = useRef<HTMLDivElement>(null);

  // Add computed Status field for sorting
  const announcementsWithStatus = useMemo(
    () =>
      announcements.map((a) => ({
        ...a,
        Status: getStatus(a.ExpireDate),
      })),
    [announcements],
  );

  const sortedAnnouncements = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => stableSort(announcementsWithStatus, getComparator(state.order, state.orderBy) as any) as (Announcement & { Status: string })[],
    [announcementsWithStatus, state.order, state.orderBy],
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [sortedAnnouncements]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let scrollContainer: HTMLElement | null = el.parentElement;
    while (scrollContainer) {
      const { overflow, overflowY } = getComputedStyle(scrollContainer);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') break;
      scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer!;
      const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

      if (nearBottom && !isLoadingMore && visibleCount < sortedAnnouncements.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedAnnouncements.length));
          setIsLoadingMore(false);
        }, 100);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer!.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visibleCount, sortedAnnouncements.length]);

  const visibleRows = useMemo(
    () => sortedAnnouncements.slice(0, visibleCount),
    [sortedAnnouncements, visibleCount],
  );

  const totalCols = state.headTableCells.length;

  return (
    <Paper ref={listRef} elevation={1}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing {visibleRows.length} of {sortedAnnouncements.length} announcements
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {state.headTableCells.map((cell) => {
                const isActive = state.orderBy === cell.property;
                const direction = isActive ? state.order : 'asc';

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const sxProps: any = {
                  ...(cell.width ? { width: cell.width } : {}),
                  ...(cell.hideOnMobile ? { display: { xs: 'none', md: 'table-cell' } } : {}),
                };

                return (
                  <TableCell
                    key={cell.id}
                    sortDirection={isActive ? state.order : false}
                    sx={Object.keys(sxProps).length > 0 ? sxProps : undefined}
                  >
                    {cell.property ? (
                      <TableSortLabel
                        active={isActive}
                        direction={direction}
                        onClick={handler.onSort(cell.property)}
                      >
                        {cell.label}
                      </TableSortLabel>
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={totalCols} align="center" sx={{ py: 6 }}>
                  <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    No announcements found
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {visibleRows.map((announcement) => {
              const isOpen = state.selectedIds.includes(announcement.ItemID);
              const status = announcement.Status;

              return (
                <React.Fragment key={announcement.ItemID}>
                  <TableRow
                    hover
                    onClick={() => handler.onToggleExpand(announcement.ItemID)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <IconButton
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        aria-expanded={isOpen}
                        size="small"
                      >
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{announcement.Title}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{announcement.CreatedByUser}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(announcement.CreatedDate)}</TableCell>
                    <TableCell>{formatDate(announcement.ExpireDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={status}
                        size="small"
                        color={status === 'Active' ? 'success' : 'default'}
                        variant={status === 'Active' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }} colSpan={totalCols}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <AnnouncementFormUnified
                            mode="edit"
                            announcement={announcement}
                            onSaveSuccess={onRefresh}
                            onDelete={onDelete}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={totalCols}>
                  <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!isLoadingMore && visibleRows.length >= sortedAnnouncements.length && sortedAnnouncements.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            End of list
          </Typography>
        )}
      </Box>

    </Paper>
  );
}

export default AnnouncementList;
